import { useEffect, useState } from "react";

import { BRAND_ENDPOINTS } from "@constants/endpoints";
import useAxios from "@hooks/use-axios";

const DEFAULT_LIMIT = 10;

// Every publisher invoice raised against this brand. A self-payout brand pays these
// itself, so this list is its payables queue — the counterpart of the debit-note
// screen a managed-payout brand gets.
const useBrandDisbursements = (brandId) => {
    const { axiosInstance, loading } = useAxios({ base: "brand", initialLoader: true });
    const [invoices, setInvoices] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: DEFAULT_LIMIT });
    // Sum across the whole set, not just the page the table is showing.
    const [totalAmount, setTotalAmount] = useState(0);
    const [utrTarget, setUtrTarget] = useState(null);
    const [revertTarget, setRevertTarget] = useState(null);

    const fetchInvoices = async (page = 1, limit = DEFAULT_LIMIT) => {
        if (!brandId) return;
        try {
            const res = await axiosInstance.get(BRAND_ENDPOINTS.INVOICES(brandId), { params: { page, limit } });
            const result = res.data || res;
            const items = result.items || result.data || result.results || [];
            setInvoices(items);
            setPagination({
                total: result.total ?? items.length,
                page : result.page ?? page,
                limit: result.limit ?? limit
            });
            setTotalAmount(result.total_amount ?? 0);
        } catch {
            setInvoices([]);
            setPagination({ total: 0, page, limit });
            setTotalAmount(0);
        }
    };

    useEffect(() => {
        fetchInvoices(1, DEFAULT_LIMIT);
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePageChange = (limit, page) => fetchInvoices(page, limit);
    const handlePerRowsChange = (limit) => fetchInvoices(1, limit);

    const openUtrModal = (invoice) => setUtrTarget(invoice);
    const closeUtrModal = () => setUtrTarget(null);
    const openRevertModal = (invoice) => setRevertTarget(invoice);
    const closeRevertModal = () => setRevertTarget(null);

    // Same two calls the ops disbursement queue makes, account-scoped: the modal hands
    // back { utr, payment_date, mode } and the API reads payment_reference/date/mode.
    const uploadUTR = async (payload) => {
        await axiosInstance.patch(BRAND_ENDPOINTS.MARK_INVOICE_PAID(brandId, utrTarget._id), {
            payment_reference: payload.utr,
            payment_date     : payload.payment_date,
            payment_mode     : payload.mode
        });
        await fetchInvoices(pagination.page, pagination.limit);
    };

    const revertPayment = async (reason) => {
        await axiosInstance.patch(BRAND_ENDPOINTS.REVERT_INVOICE_PAYMENT(brandId, revertTarget._id), { reason });
        await fetchInvoices(pagination.page, pagination.limit);
    };

    return {
        invoices,
        loading,
        pagination,
        totalAmount,
        handlePageChange,
        handlePerRowsChange,
        utrTarget,
        revertTarget,
        openUtrModal,
        closeUtrModal,
        openRevertModal,
        closeRevertModal,
        uploadUTR,
        revertPayment
    };
};

export default useBrandDisbursements;
